import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import WebFont from 'webfontloader';

import '@core/ui';
import { ConfigProvider, productFeatures } from './shared/index.js';
import { useConfigStore } from './stores/index.js';
import App from './App.jsx';

/**
 * Einstiegspunkt beider Frontends.
 *
 * Die App-Hülle reicht nur ihren Produktdeskriptor herein und importiert ihr globales SCSS
 * selbst; alles andere — Provider, Router, Service-Worker-Abbau — ist hier geteilt.
 *
 * @param {object} product Produktdeskriptor aus `apps/<app>/src/product.js`.
 */
export const renderApp = (product) => {
    // Die Fähigkeiten des Backends gehören vor den ersten Render: der Config-Store entsteht auf
    // Modulebene, dort ist der Deskriptor nicht verfügbar, und `/config` wird beim Mount geholt.
    // Die Werte kommen aus `config/app.config.json` (`products.<id>`, per `define` durchgereicht).
    void useConfigStore.getState().configureFeatures(productFeatures);

    // Todo: Better place for WebFont Loader?
    if (process.env.REACT_APP_FONT_URL) {
        WebFont.load({
            custom: { families: [process.env.REACT_APP_FONT_FAMILIES], urls: [process.env.REACT_APP_FONT_URL] },
        });
    }

    if ('OTPCredential' in window) {
        const abortController = new AbortController();
        navigator.credentials
            .get({ otp: { transport: ['sms'] }, signal: abortController.signal })
            .then((credential) => {
                if (credential?.code) {
                    localStorage.setItem('webOtpCode', JSON.stringify(credential.code));
                }
            })
            .catch((err) => {
                console.log('OTP credential resolving failed with:', err);
            });
    }

    const app = (
        <React.StrictMode>
            <ConfigProvider>
                <BrowserRouter>
                    <App product={product} />
                </BrowserRouter>
            </ConfigProvider>
        </React.StrictMode>
    );

    createRoot(document.getElementById('root')).render(app);

    // Unregister all active service workers to completely remove existing installations (PROD-456)
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker
            .getRegistrations()
            .then((registrations) => {
                for (const registration of registrations) {
                    registration.unregister();
                }
            })
            .catch((err) => {
                console.error('Service Worker unregistration failed: ', err);
            });
    }

    // Clear all cache storage to prevent stale cached files from being used (PROD-456)
    if ('caches' in window) {
        caches
            .keys()
            .then((keys) => {
                for (const key of keys) {
                    caches.delete(key);
                }
            })
            .catch((err) => {
                console.error('Cache storage clearing failed: ', err);
            });
    }
};

export default renderApp;
